/**
 * DG Shipping rank → required certificate canonicalIds matrix.
 *
 * Based on STCW 2010 (as amended), DG Shipping circular requirements,
 * and standard Indian maritime industry practice.
 *
 * canonicalIds come from certificate-catalog.ts. "required" = must hold to sail
 * in that rank. "recommended" = commonly required by employers/flag state.
 */

export type RankTier =
  | 'master'
  | 'chief_officer'
  | 'officer_deck'          // 2nd / 3rd officer, OOW
  | 'chief_engineer'
  | 'second_engineer'
  | 'officer_engine'        // 3rd / 4th engineer, EOOW
  | 'eto'
  | 'rating';

export type RankRequirement = {
  canonicalId: string;
  label: string;
  critical: boolean;        // true = no exemption; false = common but employer/vessel specific
};

// ── Universal — every seafarer regardless of rank ─────────────────────────────

const UNIVERSAL: RankRequirement[] = [
  { canonicalId: 'indos',         label: 'INDoS Registration',          critical: true },
  { canonicalId: 'cdc',           label: 'Continuous Discharge Certificate (CDC)', critical: true },
  { canonicalId: 'passport',      label: 'Passport',                    critical: true },
  { canonicalId: 'medical',       label: 'ENG1 Medical Fitness',        critical: true },
  { canonicalId: 'pst',           label: 'Personal Survival Techniques (PST)',    critical: true },
  { canonicalId: 'fpff',          label: 'Fire Prevention & Fire Fighting (FPFF)', critical: true },
  { canonicalId: 'pssr',          label: 'Personal Safety & Social Responsibilities (PSSR)', critical: true },
  { canonicalId: 'pscrb',         label: 'Proficiency in Survival Craft & Rescue Boats (PSCRB)', critical: true },
  { canonicalId: 'ship_security_awareness', label: 'Ship Security Awareness',   critical: true },
];

// ── Deck branch ────────────────────────────────────────────────────────────────

const DECK_OFFICER_COMMON: RankRequirement[] = [
  ...UNIVERSAL,
  { canonicalId: 'efa',           label: 'Elementary First Aid (EFA)',           critical: true },
  { canonicalId: 'radar_arpa',    label: 'Radar / ARPA',                          critical: true },
  { canonicalId: 'crowd_management', label: 'Crowd Management',                   critical: false },
];

const MASTER_REQUIREMENTS: RankRequirement[] = [
  ...DECK_OFFICER_COMMON,
  { canonicalId: 'coc_master',    label: 'Certificate of Competency — Master (Class I Navigation)', critical: true },
  { canonicalId: 'goc',           label: 'GMDSS General Operator Certificate (GOC)',  critical: true },
  { canonicalId: 'aff',           label: 'Advanced Fire Fighting (AFF)',           critical: true },
  { canonicalId: 'medical_care',  label: 'Medical Care (STCW A-VI/4-2)',           critical: true },
  { canonicalId: 'sso',           label: 'Ship Security Officer (SSO)',             critical: true },
  { canonicalId: 'brm',           label: 'Bridge Resource Management (BRM)',        critical: false },
  { canonicalId: 'arpa_management', label: 'ARPA — Management Level',             critical: false },
];

const CHIEF_OFFICER_REQUIREMENTS: RankRequirement[] = [
  ...DECK_OFFICER_COMMON,
  { canonicalId: 'coc_chief_officer', label: 'CoC — Chief Mate (Class II Navigation)', critical: true },
  { canonicalId: 'aff',           label: 'Advanced Fire Fighting (AFF)',           critical: true },
  { canonicalId: 'mfa',           label: 'Medical First Aid (MFA)',                critical: true },
  { canonicalId: 'brm',           label: 'Bridge Resource Management (BRM)',       critical: false },
  { canonicalId: 'arpa_operational', label: 'ARPA — Operational Level',           critical: false },
];

const OFFICER_DECK_REQUIREMENTS: RankRequirement[] = [
  ...DECK_OFFICER_COMMON,
  { canonicalId: 'coc_oow',       label: 'CoC — Watchkeeping Officer (Class III/IV Navigation)', critical: true },
  { canonicalId: 'roc',           label: 'GMDSS Restricted Operator Certificate (ROC)', critical: true },
  { canonicalId: 'mfa',           label: 'Medical First Aid (MFA)',                critical: false },
];

// ── Engine branch ──────────────────────────────────────────────────────────────

const ENGINE_OFFICER_COMMON: RankRequirement[] = [
  ...UNIVERSAL,
  { canonicalId: 'mfa',           label: 'Medical First Aid (MFA)',                critical: true },
];

const CHIEF_ENGINEER_REQUIREMENTS: RankRequirement[] = [
  ...ENGINE_OFFICER_COMMON,
  { canonicalId: 'coc_chief_engineer', label: 'CoC — Chief Engineer (Class I Motor/Steam)', critical: true },
  { canonicalId: 'aff',           label: 'Advanced Fire Fighting (AFF)',           critical: true },
  { canonicalId: 'medical_care',  label: 'Medical Care (STCW A-VI/4-2)',           critical: false },
  { canonicalId: 'erm',           label: 'Engine Room Resource Management (ERM)',  critical: false },
];

const SECOND_ENGINEER_REQUIREMENTS: RankRequirement[] = [
  ...ENGINE_OFFICER_COMMON,
  { canonicalId: 'coc_second_engineer', label: 'CoC — 2nd Engineer (Class II Motor/Steam)', critical: true },
  { canonicalId: 'aff',           label: 'Advanced Fire Fighting (AFF)',           critical: false },
  { canonicalId: 'erm',           label: 'Engine Room Resource Management (ERM)',  critical: false },
];

const OFFICER_ENGINE_REQUIREMENTS: RankRequirement[] = [
  ...ENGINE_OFFICER_COMMON,
  { canonicalId: 'coc_engineer',  label: 'CoC — Engineer Officer (Class III/IV Motor/Steam)', critical: true },
];

const ETO_REQUIREMENTS: RankRequirement[] = [
  ...UNIVERSAL,
  { canonicalId: 'eto_cert',      label: 'Electro-Technical Officer Certificate', critical: true },
  { canonicalId: 'mfa',           label: 'Medical First Aid (MFA)',               critical: false },
];

const RATING_REQUIREMENTS: RankRequirement[] = [
  ...UNIVERSAL,
];

// ── Rank → tier mapping ────────────────────────────────────────────────────────

const RANK_TIER_PATTERNS: Array<{ pattern: RegExp; tier: RankTier }> = [
  // Master / Captain — check before generic "officer" patterns
  { pattern: /\bmaster\s+mariner\b|\bmaster\b|\bcaptain\b|\bcmdr\b|\bcommanding\b/i,        tier: 'master' },
  // Chief Officer / Chief Mate
  { pattern: /\bchief\s+(?:officer|mate)\b|\bch\.?\s*off\b|\bc\/o\b/i,                     tier: 'chief_officer' },
  // Deck officers — 2nd / 3rd officer, OOW, navigating officer
  { pattern: /\b(?:2nd|second|3rd|third|junior|navigating|deck)\s+officer\b|\boow\b|\bwatchkeep/i, tier: 'officer_deck' },
  // Chief Engineer — check before generic "engineer" patterns
  { pattern: /\bchief\s+engineer\b|\bc\.?e\.?\b|\bchief\s+eng\b/i,                         tier: 'chief_engineer' },
  // Second Engineer
  { pattern: /\b(?:2nd|second)\s+engineer\b|\b2\/e\b/i,                                    tier: 'second_engineer' },
  // Junior engineers — 3rd / 4th engineer, EOOW, QMED
  { pattern: /\b(?:3rd|third|4th|fourth|junior|engine)\s+engineer\b|\beoow\b|\bqmed\b/i,   tier: 'officer_engine' },
  // ETO
  { pattern: /\belectro[\s-]?technical\b|\beto\b/i,                                        tier: 'eto' },
  // Ratings — Bosun, Boatswain, AB, OS, Fitter, Motorman, Wiper, Cadet, Trainee
  // These fall through to the 'rating' fallback below, but explicit patterns
  // here prevent matching a higher tier via partial-word overlap.
  { pattern: /\bbosun\b|\bboatswain\b|\bable\s+seaman\b|\b\bab\b|\bordinary\s+seaman\b|\bos\b|\bfitter\b|\bmotorman\b|\bwiper\b|\bcadet\b|\btrainee\b/i, tier: 'rating' },
];

export function detectRankTier(rank: string): RankTier | null {
  if (!rank) return null;
  for (const { pattern, tier } of RANK_TIER_PATTERNS) {
    if (pattern.test(rank)) return tier;
  }
  // Ratings / cadets / unrecognized → show universal only
  return 'rating';
}

export const TIER_REQUIREMENTS: Record<RankTier, RankRequirement[]> = {
  master:           MASTER_REQUIREMENTS,
  chief_officer:    CHIEF_OFFICER_REQUIREMENTS,
  officer_deck:     OFFICER_DECK_REQUIREMENTS,
  chief_engineer:   CHIEF_ENGINEER_REQUIREMENTS,
  second_engineer:  SECOND_ENGINEER_REQUIREMENTS,
  officer_engine:   OFFICER_ENGINE_REQUIREMENTS,
  eto:              ETO_REQUIREMENTS,
  rating:           RATING_REQUIREMENTS,
};

export const TIER_LABELS: Record<RankTier, string> = {
  master:           'Master / Captain',
  chief_officer:    'Chief Officer / Chief Mate',
  officer_deck:     'Deck Officer (2nd/3rd Officer)',
  chief_engineer:   'Chief Engineer',
  second_engineer:  'Second Engineer',
  officer_engine:   'Engine Officer (3rd/4th Engineer)',
  eto:              'Electro-Technical Officer',
  rating:           'Rating / Cadet',
};
